import { Button } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { VyIcon } from '@weft/icons';
import type { PromotionInput } from '@weft/types';
import { isNotEmptyArray, isNotNullOrUndefined, withDefault } from '@weft/utils';
import React from 'react';
import type { FieldPath, UseFormGetValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { VayuChip } from '../../../../../../components/design/vayu-theme-components/VayuChip';
import { useDialog } from '../../../../../../hooks/use-dialog';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { FieldPathMetadata } from '../../FixedChargesEditTable/fixed-charges-edit-table-types/field-path-metadata';
import { PromotionChargeEditDrawer } from '../../PromotionChargeEditDrawer/PromotionChargeEditDrawer';

type PromotionChargeAddonsCellProps = {
  promotion: PromotionInput;
};

export const PromotionChargeAddonsCell: React.FC<PromotionChargeAddonsCellProps> = ({ promotion }) => {
  const { isDialogOpen: isDrawerOpen, handleOpen, handleClose } = useDialog();
  const { getValues } = useFormContext<PlanInputForm, NewPlanForm>();
  const promotions = withDefault(getValues('promotions'), []);
  const promotionIndex = promotions.findIndex(({ id }) => id === promotion.id);

  const addedFieldsLabels = getAdjustedAddedFieldsLabels(promotion, promotionIndex, getValues);

  if (isNotEmptyArray(addedFieldsLabels)) {
    return (
      <>
        <div className="flex gap-x-1">
          {addedFieldsLabels.map(({ label, tooltip }) => isNotNullOrUndefined(tooltip) ? (
            <Tooltip
              key={label}
              title={tooltip.title}
              placement="top"
              arrow
            >
              <VayuChip
                label={label}
                onClick={handleOpen}
              />
            </Tooltip>
          ) : (
            <VayuChip
              key={label}
              label={label}
              onClick={handleOpen}
            />
          ))}
        </div>

        <PromotionChargeEditDrawer
          open={isDrawerOpen}
          onClose={handleClose}
          promotion={promotion}
        />
      </>
    );
  }

  return (
    <>
      <Button
        className="min-w-fit"
        sx={{
          color: '#576063',
          background: isDrawerOpen ? '#E1E6E8' : '#F0F4F6',
          fontWeight: '500',
          fontSize: '14px',
          boxShadow: 'none',
          columnGap: '10px',
          borderRadius: '6px',
          padding: '6px 12px',
        }}
        onClick={handleOpen}
      >
        <VyIcon
          color="#576063"
          icon="Edit2"
        />
        <span>Modify</span>
      </Button>

      <PromotionChargeEditDrawer
        open={isDrawerOpen}
        onClose={handleClose}
        promotion={promotion}
      />
    </>
  );
};

function getAdjustedAddedFieldsLabels(promotion: PromotionInput, promotionIndex: number, getValues: UseFormGetValues<PlanInputForm>) {
  const addedFieldsLabels = getAddedFieldsPathMetadata(promotion, promotionIndex, getValues);

  if (addedFieldsLabels.length > 2) {
    return [
      ...addedFieldsLabels.slice(0, 2),
      {
        path: '' as FieldPath<PromotionInput>,
        label: '+1',
      },
    ] satisfies FieldPathMetadata<PromotionInput>[];
  }

  return addedFieldsLabels;
}

function getAddedFieldsPathMetadata(promotion: PromotionInput, promotionIndex: number, getValues: UseFormGetValues<PlanInputForm>) {
  const fieldsPathsWithLabels = getFieldsPathsWithLabels(promotion, promotionIndex, getValues);

  return fieldsPathsWithLabels.filter(({ path }) => {
    const value = getValues(`promotions.${promotionIndex}.${path}`);

    return isNotNullOrUndefined(value);
  });
}

function getFieldsPathsWithLabels(_promotion: PromotionInput, _promotionIndex: number, _getValues: UseFormGetValues<PlanInputForm>): FieldPathMetadata<PromotionInput>[] {
  return [];
}